import { getRelationshipLabel } from '@fs/zion-sls'
import { i18n } from '@fs/zion-locale'
import { getCommonRelationships } from '../../../../views/indexing/components/editPanels/editRelationships/utils/EditRelationships.utls'
import { MAKE_NEW_RECORD, NO_RELATION_TYPE } from '../../shared/relationships/relationshipConstants'
import { parseRelKey } from '../../shared/relationships/relationshipQueries'

export const NO_RELATION_SUGGESTION = {
  key: NO_RELATION_TYPE,
  primaryText: i18n.t('types.no_relation', 'No Relation'),
  group: i18n.t('household-details-card.other-group', 'Other Relationships'),
  sort: 3,
}

export const prepareRelationshipsFields = (relationships, isCreatingNewPerson = false) => {
  let formIdCounter = 1

  const relDefaults =
    relationships?.map((rel) => {
      const { relKey, order } = parseRelKey(rel?.relKey)

      return {
        formId: `rel_${formIdCounter++}`,
        slsRelationshipId: rel?.id || null,
        personId: rel?.otherPersonId || null,
        recordId: rel?.person?.recordId || null,
        relationshipType: relKey || null,
        relationshipDirection: order || null,
        relationship: rel?.label && !isCreatingNewPerson ? { key: rel?.relKey, primaryText: rel?.label } : null,
        value: rel?.value ? { key: rel?.otherPersonId, primaryText: rel?.value } : null,
        isNew: !rel?.id && rel?.relKey !== NO_RELATION_TYPE,
        isDeleted: false,
        secondaryPersonPrimaryStatusChange: null,
      }
    }) || []

  // Also keep track of original relationships for the save function
  const relationshipsById = {}
  relationships
    ?.filter((rel) => rel.fieldType !== 'NO_RELATION') // Exclude NO_RELATION entries
    ?.forEach((rel) => {
      if (rel?.id) {
        const { relKey, order } = parseRelKey(rel?.relKey)
        relationshipsById[rel.id] = {
          id: rel.id,
          otherPersonId: rel.otherPersonId,
          relKey,
          order,
        }
      }
    })

  return { relationships: relDefaults, relationshipsById }
}

/**
 * Build default form values for the family members edit UI following the simplified form state structure.
 *
 * Creates a form state that matches the structure defined in USER_INTERACTION.md:
 * - focusedPerson: Contains personId and recordId
 * - relationships: Array of relationship objects with stable formIds
 * - crossRecordResolution: Tracks cross-record strategy decisions
 *
 * @param {Array<Object>} relationships Raw relationships; items may contain: id, relKey, label, value, otherPersonId.
 * @param {Object} person The focused person object with id and recordId.
 * @param {Array<Object>} households Array of household suggestion objects for the household field.
 * @param {boolean} isCreatingNewPerson Whether the card is being used in the context of creating a new person (vs editing an existing one).
 * This affects how we determine the default relationship options selected in the form.
 * @returns {Object} Initial form values object matching the simplified form state structure.
 */
export function prepareFamilyMembersDefaultValues(relationships, person, households = [], isCreatingNewPerson = false) {
  const { relationships: relDefaults, relationshipsById } = prepareRelationshipsFields(
    relationships,
    isCreatingNewPerson
  )

  // Find the household matching the person's recordId or default to MAKE_NEW_RECORD
  const household =
    households.find((hh) => hh.key === person?.recordId) ?? households.find((hh) => hh.key === MAKE_NEW_RECORD)

  return {
    focusedPerson: {
      personId: person?.id || null,
      recordId: person?.recordId || null,
    },
    relationships: relDefaults,
    targetRecordId: person?.recordId || MAKE_NEW_RECORD,
    targetPersonIsPrimary: person?.primary || false,
    names: [],
    // Keep this for backward compatibility with save function
    relationshipsById,
    household,
  }
}

/**
 * Produce grouped & localized autosuggest options for relationship types respecting directionality.
 *
 * For each relationship type definition in `types.relationships`, generates direction-based entries:
 *  - FIRST and SECOND (when they resolve to distinct labels via `getRelationshipLabel`).
 * Relationships deemed "common" (using {@link getCommonRelationships}) are grouped under
 * "Common Relationships" and sorted before "Other Relationships". Labels are localized using `t`.
 *
 * @param {Object} types Types object from SLS containing an array `relationships`.
 * @param {string} sexValue GEDCOM-X sex code suffix (e.g. 'Male', 'Female').
 * @param {boolean} [includeNoRelation=false] Whether to include a "No Relation" option.
 * @returns {Array<Object>} Sorted suggestion list objects: { key, primaryText, group, sort }.
 */
export const getGroupedGenderedRelTypeSuggestions = (types, sexValue, includeNoRelation = true) => {
  const commonTypes = getCommonRelationships()
  const groupedSuggestions = types?.relationships?.reduce((acc, rel) => {
    const otherPerson = {
      subElements: [
        {
          fieldType: 'SEX_CODE',
          fieldValues: [
            {
              normalizedValues: [
                {
                  resource: `http://gedcomx.org/${sexValue}`,
                },
              ],
            },
          ],
        },
      ],
    }
    let firstRelationshipLabel = ''
    let secondRelationshipLabel = ''
    firstRelationshipLabel = getRelationshipLabel({ otherPerson, order: 'FIRST', typeData: rel })
    secondRelationshipLabel = getRelationshipLabel({ otherPerson, order: 'SECOND', typeData: rel })

    const isCommon = commonTypes.some((common) => common === rel.relType)
    let group = i18n.t('household-details-card.other-group', 'Other Relationships')
    let sort = 2
    if (isCommon) {
      group = i18n.t('household-details-card.common-group', 'Common Relationships')
      sort = 1
    }

    acc.push({
      key: `${rel.relType}_FIRST`,
      primaryText: firstRelationshipLabel,
      group,
      sort,
    })
    if (firstRelationshipLabel !== secondRelationshipLabel) {
      acc.push({
        key: `${rel.relType}_SECOND`,
        primaryText: secondRelationshipLabel,
        group,
        sort,
      })
    }
    return acc
  }, [])
  groupedSuggestions.sort((a, b) => {
    if (a.group === b.group) {
      return a.primaryText.localeCompare(b.primaryText)
    }
    return a.sort - b.sort
  })

  // Optionally include "No Relation" at the end
  if (includeNoRelation) {
    groupedSuggestions.push(NO_RELATION_SUGGESTION)
  }

  return groupedSuggestions
}

/**
 * @typedef {Object} PendingReviewResult
 * @property {Array<string>} newMemberIds - Person IDs that need review.
 * @property {Array<string>} newMemberIdsWithExistingRelationships - Person IDs with existing relationships in their households.
 */
